<!-- Page Header -->
<div class="mb-4">
  <h1 class="h2 mb-1 fw-bold">{{ pageTitle }}</h1>
  <p class="text-muted mb-0">
    <span *ngIf="isRenewalMode">Create a new certification record while preserving the previous version</span>
    <span *ngIf="!isRenewalMode && isEditMode">Update certification details</span>
    <span *ngIf="!isRenewalMode && !isEditMode">Add a new certification to your {{ ownerTypeParam === 'profile' ? 'personal' : 'organization' }} records</span>
  </p>
</div>

<div class="row">
  <div class="col-12 col-lg-8">
    <form [formGroup]="certForm" (ngSubmit)="onSubmit()">

      <!-- Renewal Notice -->
      <div class="alert alert-info d-flex align-items-start mb-3 py-2 px-3" *ngIf="isRenewalMode && previousCertNumber">
        <i class="bi bi-info-circle me-2 mt-1"></i>
        <div>
          <span class="small">
            You are renewing certification <strong>{{ previousCertNumber }}</strong>.
            The previous record will be archived and linked to this new certification.
          </span>
        </div>
      </div>

      <!-- Certification Type Card -->
      <div class="card mb-3">
        <div class="card-header p-3">
          <h5 class="mb-0">Certification Type</h5>
        </div>
        <div class="card-body p-3">
          <div class="row g-3">
            <div class="col-12">
              <label for="certificationType" class="form-label fw-medium">Type <span class="text-danger">*</span></label>
              <select
                class="form-select"
                id="certificationType"
                formControlName="certificationType"
                [class.is-invalid]="certForm.get('certificationType')?.invalid && certForm.get('certificationType')?.touched"
              >
                <option value="">Select Certification Type</option>
                <option *ngFor="let type of availableCertTypes" [value]="type.value">
                  {{ type.label }}
                </option>
              </select>
              <div class="invalid-feedback" *ngIf="certForm.get('certificationType')?.invalid && certForm.get('certificationType')?.touched">
                Certification type is required
              </div>
              <div class="form-text" *ngIf="selectedCertType">
                {{ selectedCertType.description }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Certificate Details Card -->
      <div class="card mb-3">
        <div class="card-header p-3">
          <h5 class="mb-0">Certificate Details</h5>
        </div>
        <div class="card-body p-3">
          <div class="row g-3">
            <div class="col-12 col-md-6">
              <label for="certificateNumber" class="form-label fw-medium">Certificate Number <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                id="certificateNumber"
                formControlName="certificateNumber"
                [class.is-invalid]="certForm.get('certificateNumber')?.invalid && certForm.get('certificateNumber')?.touched"
                placeholder="Enter certificate number"
              >
              <div class="invalid-feedback" *ngIf="certForm.get('certificateNumber')?.invalid && certForm.get('certificateNumber')?.touched">
                Certificate number is required
              </div>
              <div class="form-text" *ngIf="isRenewalMode && previousCertNumber">
                Previous: {{ previousCertNumber }}. For FAA 44807, append the renewal letter (A, B, C, etc.)
              </div>
            </div>

            <div class="col-12 col-md-6">
              <label for="issuingAuthority" class="form-label fw-medium">Issuing Authority <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                id="issuingAuthority"
                formControlName="issuingAuthority"
                [class.is-invalid]="certForm.get('issuingAuthority')?.invalid && certForm.get('issuingAuthority')?.touched"
                placeholder="e.g., Federal Aviation Administration"
              >
              <div class="invalid-feedback" *ngIf="certForm.get('issuingAuthority')?.invalid && certForm.get('issuingAuthority')?.touched">
                Issuing authority is required
              </div>
            </div>

            <div class="col-12 col-md-6">
              <label for="issuedDate" class="form-label fw-medium">Issue Date <span class="text-danger">*</span></label>
              <input
                type="date"
                class="form-control"
                id="issuedDate"
                formControlName="issuedDate"
                [class.is-invalid]="certForm.get('issuedDate')?.invalid && certForm.get('issuedDate')?.touched"
              >
              <div class="invalid-feedback" *ngIf="certForm.get('issuedDate')?.invalid && certForm.get('issuedDate')?.touched">
                Issue date is required
              </div>
            </div>

            <div class="col-12 col-md-6">
              <label for="expirationDate" class="form-label fw-medium">Expiration Date</label>
              <input
                type="date"
                class="form-control"
                id="expirationDate"
                formControlName="expirationDate"
                max="2999-12-31"
              />
              <div class="form-text">Leave blank if the certification does not expire</div>
            </div>

            <!-- State field for state-specific licenses -->
            <div class="col-12 col-md-6" *ngIf="isStateRequired">
              <label for="state" class="form-label fw-medium">State <span class="text-danger">*</span></label>
              <select
                class="form-select"
                id="state"
                formControlName="state"
                [class.is-invalid]="certForm.get('state')?.invalid && certForm.get('state')?.touched"
              >
                <option value="">Select State</option>
                <option *ngFor="let state of usStates" [value]="state.value">
                  {{ state.label }}
                </option>
              </select>
              <div class="invalid-feedback" *ngIf="certForm.get('state')?.invalid && certForm.get('state')?.touched">
                State is required for state-issued licenses
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Document Upload Card -->
      <div class="card mb-3">
        <div class="card-header p-3">
          <h5 class="mb-0">Document Upload</h5>
        </div>
        <div class="card-body p-3">
          <div class="row g-3">
            <div class="col-12">
              <label for="documentFile" class="form-label fw-medium">Certificate Document</label>
              <input
                type="file"
                class="form-control"
                id="documentFile"
                (change)="onFileSelected($event)"
                accept=".pdf,.jpg,.jpeg,.png"
              >
              <div class="form-text">Upload a PDF, JPEG, or PNG of your certificate (max 20MB)</div>
            </div>

            <!-- Show existing document if editing -->
            <div class="col-12" *ngIf="isEditMode && existingCertification?.documentUrl">
              <div class="d-flex align-items-center gap-2 p-2 bg-light rounded">
                <i class="bi bi-file-earmark-pdf text-danger"></i>
                <span class="small">{{ existingCertification?.documentFileName || 'Current document' }}</span>
                <a href="javascript:void(0)" (click)="viewDocument()" class="small ms-auto">View</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Notes Card -->
      <div class="card mb-3">
        <div class="card-header p-3">
          <h5 class="mb-0">Additional Notes</h5>
        </div>
        <div class="card-body p-3">
          <div class="row g-3">
            <div class="col-12">
              <label for="notes" class="form-label fw-medium">Notes</label>
              <textarea
                class="form-control"
                id="notes"
                formControlName="notes"
                rows="3"
                appCharCounter
                placeholder="Any additional information about this certification..."
                [class.is-invalid]="certForm.get('notes')?.invalid && certForm.get('notes')?.touched"
              ></textarea>
              <div class="invalid-feedback" *ngIf="certForm.get('notes')?.hasError('maxlength') && certForm.get('notes')?.touched">
                Notes cannot exceed {{ maxNotesLength }} characters.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div class="alert alert-danger" *ngIf="errorMessage">
        {{ errorMessage }}
      </div>

      <!-- Form Actions -->
      <div class="d-flex gap-3 mb-4">
        <button
          type="submit"
          class="btn btn-primary"
          [disabled]="isLoading"
        >
          <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          {{ submitButtonText }}
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          (click)="onCancel()"
          [disabled]="isLoading"
        >
          Cancel
        </button>
      </div>

    </form>
  </div>

  <!-- Sidebar with helpful information -->
  <div class="col-12 col-lg-4">
    <div class="card">
      <div class="card-header p-3">
        <h5 class="mb-0">Certification Information</h5>
      </div>
      <div class="card-body p-3">
        <div class="d-flex flex-column gap-3">
          <div>
            <h6 class="fw-bold mb-2">
              <i class="bi bi-shield-check me-2 sidebar-icon"></i>
              Secure Storage
            </h6>
            <p class="small text-muted mb-0">
              All certification documents are encrypted and stored securely.
            </p>
          </div>

          <div>
            <h6 class="fw-bold mb-2">
              <i class="bi bi-clock-history me-2 sidebar-icon"></i>
              Renewal History
            </h6>
            <p class="small text-muted mb-0">
              When you renew a certification, the previous record is archived and linked for audit purposes.
            </p>
          </div>

          <div>
            <h6 class="fw-bold mb-2">
              <i class="bi bi-bell me-2 sidebar-icon"></i>
              Expiration Alerts
            </h6>
            <p class="small text-muted mb-0">
              You'll receive notifications 30, 14, and 7 days before a certification expires.
            </p>
          </div>

          <div *ngIf="selectedCertType?.value === 'faa-44807'">
            <h6 class="fw-bold mb-2">
              <i class="bi bi-info-circle me-2 sidebar-icon"></i>
              FAA 44807 Renewals
            </h6>
            <p class="small text-muted mb-0">
              FAA 44807 renewals append a letter to the certificate number: A for year 1, B for year 2, etc.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
